import { Component, OnInit, OnDestroy, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Subscription, switchMap, firstValueFrom } from 'rxjs';
import { TranslocoModule } from '@jsverse/transloco';
import { StepperModule } from 'primeng/stepper';
import { ButtonModule } from 'primeng/button';
import { GreentripperLoaderComponent } from '../../shared/components/greentripper-loader/greentripper-loader.component';
import { MessageModule } from 'primeng/message';

import { PageHeaderComponent, BreadcrumbItem } from '../../shared/components/page-header/page-header.component';
import { ImpactService } from '../../impact/services/impact.service';
import { ContributeWizardService, WizardUrlState } from './services/contribute-wizard.service';
import { PaymentStateService } from './services/payment-state.service';
import { ProjectPublicService } from '../../shared/services/project-public.service';
import { Project, ProjectStatus } from '../../admin/models/project.model';
import { CurrentUserService } from '../../shared/services/current-user.service';

import { StepChooseProjectComponent } from './steps/step-choose-project/step-choose-project.component';
import { StepAdjustContributionComponent } from './steps/step-adjust-contribution/step-adjust-contribution.component';
import { StepCertificateDetailsComponent } from './steps/step-certificate-details/step-certificate-details.component';
import { StepReviewConfirmComponent } from './steps/step-review-confirm/step-review-confirm.component';
import { ContributionSuccessComponent } from './success/contribution-success.component';

@Component({
  selector: 'app-contribute-page',
  standalone: true,
  imports: [
    CommonModule,
    TranslocoModule,
    RouterModule,
    StepperModule,
    ButtonModule,
    GreentripperLoaderComponent,
    MessageModule,
    PageHeaderComponent,
    StepChooseProjectComponent,
    StepAdjustContributionComponent,
    StepCertificateDetailsComponent,
    StepReviewConfirmComponent,
    ContributionSuccessComponent,
  ],
  templateUrl: './contribute-page.component.html',
  styles: [`
    :host {
      display: block;
    }
  `],
})
export class ContributePageComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private impactService = inject(ImpactService);
  private projectService = inject(ProjectPublicService);
  protected wizardService = inject(ContributeWizardService);
  private paymentStateService = inject(PaymentStateService);
  private currentUserService = inject(CurrentUserService);

  hasIncompleteAddress = computed(() => {
    const address = this.currentUserService.currentUser()?.company?.address;
    return !address?.street?.trim() || !address?.city?.trim() || !address?.zipCode?.trim();
  });

  breadcrumbs: BreadcrumbItem[] = [];
  loading = signal(true);
  error = signal<string | null>(null);
  projects = signal<Project[]>([]);
  projectsLoading = signal(true);
  currentStep = signal(1);
  submitting = signal(false);

  // Stripe redirect handling
  verifyingPayment = signal(false);
  paymentCanceled = signal(false);
  redirectingToStripe = signal(false);
  checkingPaymentStatus = signal(false);

  private routeSubscription?: Subscription;
  private boundOnVisibilityChange = this.onVisibilityChange.bind(this);
  private pendingUrlState: WizardUrlState | null = null;

  ngOnInit(): void {
    this.setupBreadcrumbs();
    this.loadProjects();
    this.setupVisibilityListener();

    // Check for Stripe redirect query params (these take priority)
    const queryParams = this.route.snapshot.queryParams;
    const sessionId = queryParams['session_id'];
    const contributionId = queryParams['contribution_id'];
    const canceled = queryParams['canceled'];

    if (sessionId && contributionId) {
      // Success redirect from Stripe - verify the session and clear payment state
      this.paymentStateService.clearPaymentAttempt();
      this.handleStripeSuccess(contributionId, sessionId);
      return;
    }

    if (canceled) {
      // Cancel redirect from Stripe
      this.paymentStateService.clearPaymentAttempt();
      this.handleStripeCanceled();
    }

    // Store URL state for restoration after projects load
    this.pendingUrlState = this.extractUrlState(queryParams);

    this.routeSubscription = this.route.params.subscribe({
      next: (params) => {
        const tripId = params['tripId'];
        if (tripId) {
          this.loadTrip(tripId);
        } else {
          this.error.set('No trip ID provided');
          this.loading.set(false);
        }
      },
      error: () => {
        this.error.set('Failed to load route parameters');
        this.loading.set(false);
      },
    });
  }

  /**
   * Extract wizard state from URL query params.
   */
  private extractUrlState(params: Record<string, string>): WizardUrlState {
    return {
      step: params['step'] ? parseInt(params['step'], 10) : undefined,
      projectId: params['projectId'],
      single: params['single'],
      pct: params['pct'],
      name: params['name'],
      msg: params['msg'],
    };
  }

  private handleStripeSuccess(contributionId: string, sessionId: string): void {
    this.verifyingPayment.set(true);
    this.loading.set(true);

    // First load the trip to have context
    const tripId = this.route.snapshot.params['tripId'];
    if (tripId) {
      this.impactService.getDetailedImpactReport(tripId).subscribe({
        next: (report) => {
          this.wizardService.initWithTrip(report.trip);

          // Verify the checkout session
          this.wizardService.verifyCheckoutSession(contributionId, sessionId).subscribe({
            next: (response) => {
              // Set payment method for success screen display
              this.wizardService.setPaymentMethod('CARD');

              this.verifyingPayment.set(false);
              this.loading.set(false);

              // If payment successful, contributionResult is already set by verifyCheckoutSession
              if (response.status === 'PAID') {
                // Clear query params for clean URL
                this.router.navigate([], {
                  relativeTo: this.route,
                  queryParams: {},
                  replaceUrl: true,
                });
              }
            },
            error: (err) => {
              console.error('[ContributePage] Failed to verify session:', err);
              this.verifyingPayment.set(false);
              this.loading.set(false);
              this.error.set('Failed to verify payment. Please contact support.');
            },
          });
        },
        error: () => {
          this.verifyingPayment.set(false);
          this.loading.set(false);
          this.error.set('Failed to load trip data');
        },
      });
    }
  }

  private handleStripeCanceled(): void {
    this.paymentCanceled.set(true);

    // Clear query params
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {},
      replaceUrl: true,
    });
  }

  ngOnDestroy(): void {
    this.routeSubscription?.unsubscribe();
    this.wizardService.reset();
    document.removeEventListener('visibilitychange', this.boundOnVisibilityChange);
  }

  /**
   * Setup visibility change listener to detect when user returns to this window.
   * Used to handle Arc browser opening Stripe in a new window.
   */
  private setupVisibilityListener(): void {
    document.addEventListener('visibilitychange', this.boundOnVisibilityChange);
  }

  /**
   * Handle visibility change event.
   * When user returns to this window after Stripe redirect, check payment status.
   */
  private async onVisibilityChange(): Promise<void> {
    if (document.visibilityState !== 'visible') return;
    if (!this.redirectingToStripe()) return;

    const attempt = this.paymentStateService.getPaymentAttempt();
    if (!attempt) {
      // No payment attempt stored - reset loading state
      this.resetLoadingState();
      return;
    }

    // Check payment status from backend
    this.checkingPaymentStatus.set(true);

    try {
      const { status } = await firstValueFrom(
        this.wizardService.checkPaymentStatus(attempt.contributionId)
      );

      if (status === 'PAID') {
        // Payment completed in another window - redirect to contribution detail
        this.paymentStateService.clearPaymentAttempt();
        this.router.navigate(['/home/contributions', attempt.contributionId]);
      } else {
        // Payment not completed (canceled or still pending)
        this.resetLoadingState();
        this.paymentCanceled.set(true);
      }
    } catch (err) {
      console.error('[ContributePage] Failed to check payment status:', err);
      this.resetLoadingState();
    } finally {
      this.checkingPaymentStatus.set(false);
    }
  }

  /**
   * Reset loading state to allow user to retry or continue.
   */
  private resetLoadingState(): void {
    this.loading.set(false);
    this.redirectingToStripe.set(false);
    this.submitting.set(false);
  }

  private setupBreadcrumbs(): void {
    this.breadcrumbs = [
      { label: 'Accueil', route: '/home', translationKey: 'navigation.home' },
      { label: 'Voyages', route: '/home/travel-board', translationKey: 'navigation.trips' },
      { label: 'Contribuer', translationKey: 'contribute.title' },
    ];
  }

  private loadTrip(tripId: string): void {
    this.impactService.getDetailedImpactReport(tripId).subscribe({
      next: (report) => {
        this.wizardService.initWithTrip(report.trip);

        // Update breadcrumbs with trip title
        if (report.trip.title) {
          this.breadcrumbs = [
            { label: 'Accueil', route: '/home', translationKey: 'navigation.home' },
            { label: 'Voyages', route: '/home/travel-board', translationKey: 'navigation.trips' },
            { label: report.trip.title, route: `/home/trips/${tripId}/impact` },
            { label: 'Contribuer', translationKey: 'contribute.title' },
          ];
        }

        this.loading.set(false);
      },
      error: (err) => {
        console.error('[ContributePage] Failed to load trip:', err);
        this.error.set('Failed to load trip data');
        this.loading.set(false);
      },
    });
  }

  private loadProjects(): void {
    this.projectService.listProjects({ status: ProjectStatus.ACTIVE }).subscribe({
      next: (response) => {
        this.projects.set(response.data);
        this.projectsLoading.set(false);

        // Pre-select default project if no projectId specified in URL
        const hasProjectInUrl = this.pendingUrlState?.projectId;
        if (!hasProjectInUrl) {
          this.projectService.getDefaultProject().subscribe({
            next: (defaultResponse) => {
              this.wizardService.selectProject(defaultResponse.data);
            },
            error: (err) => {
              console.warn('[ContributePage] No default project available:', err);
              // Continue without pre-selection - user will select manually
            },
          });
        }

        // Restore wizard state from URL after projects are loaded
        this.restoreStateFromUrl(response.data);
      },
      error: (err) => {
        console.error('[ContributePage] Failed to load projects:', err);
        this.projectsLoading.set(false);
      },
    });
  }

  /**
   * Restore wizard state from URL query params.
   * Called after projects are loaded so we can validate projectId.
   */
  private restoreStateFromUrl(projects: Project[]): void {
    if (!this.pendingUrlState) return;

    const urlState = this.pendingUrlState;
    this.pendingUrlState = null;

    // If there's state to restore, try to restore it
    const hasStateToRestore = urlState.projectId || urlState.name || urlState.pct || urlState.single;
    if (!hasStateToRestore) return;

    // Restore the wizard state
    const restored = this.wizardService.restoreFromParams(urlState, projects);

    if (!restored) {
      // Failed to restore (e.g., project not found) - reset to step 1
      this.clearUrlState();
      return;
    }

    // Determine the requested step
    const requestedStep = urlState.step || 1;

    // Validate we can access the requested step
    if (this.wizardService.canAccessStep(requestedStep)) {
      this.currentStep.set(requestedStep);
    } else {
      // Can't access requested step - redirect to step 1
      this.currentStep.set(1);
      this.clearUrlState();
    }
  }

  /**
   * Clear wizard-related query params from URL.
   */
  private clearUrlState(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {},
      replaceUrl: true,
    });
  }

  /**
   * Update URL with current wizard state.
   * Called when navigating between steps.
   */
  private updateUrlWithState(step: number): void {
    const stateParams = this.wizardService.getStateAsParams();
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { step: step > 1 ? step : null, ...stateParams },
      replaceUrl: true,
    });
  }

  /**
   * Handle step change from stepper navigation.
   * Updates URL to reflect current step and state.
   */
  onStepChange(newStep: number): void {
    this.currentStep.set(newStep);
    this.updateUrlWithState(newStep);
  }

  navigateToTravelBoard(): void {
    this.router.navigate(['/home/travel-board']);
  }

  onSubmit(): void {
    this.paymentCanceled.set(false);

    const isCardPayment = this.wizardService.selectedPaymentMethod() === 'CARD';

    // Show loader for all payment methods to prevent UI flash
    this.loading.set(true);
    if (isCardPayment) {
      this.redirectingToStripe.set(true);
    }

    this.submitting.set(true);

    // New flow: Create contribution → Initiate payment
    this.wizardService
      .createContributionOnly()
      .pipe(switchMap((contribution) => {
        // Save payment attempt state before initiating payment (for Arc browser multi-window handling)
        if (isCardPayment) {
          this.paymentStateService.savePaymentAttempt(contribution.id, this.wizardService.tripId());
        }
        return this.wizardService.initiatePayment();
      }))
      .subscribe({
        next: (response) => {
          if (response.paymentMethod === 'CARD' && response.checkoutUrl) {
            // SECURITY FIX: Validate checkout URL is from Stripe before redirecting
            // This prevents open redirect attacks if the API response is compromised
            try {
              const checkoutUrl = new URL(response.checkoutUrl);
              if (!checkoutUrl.hostname.endsWith('stripe.com')) {
                throw new Error('Invalid checkout URL domain');
              }
              // Redirect to Stripe Checkout (loader already showing)
              window.location.href = response.checkoutUrl;
            } catch {
              console.error('[ContributePage] Invalid Stripe checkout URL');
              this.error.set('Invalid payment redirect. Please try again.');
              this.resetLoadingState();
              this.paymentStateService.clearPaymentAttempt();
              return;
            }
          } else {
            // For BANK_TRANSFER, the wizardService has already set bankDetails
            // and contributionResult, so the success screen will be shown
            this.loading.set(false);
            this.submitting.set(false);
          }
        },
        error: (err) => {
          console.error('[ContributePage] Failed to process payment:', err);
          this.loading.set(false);
          this.redirectingToStripe.set(false);
          this.submitting.set(false);
          this.paymentStateService.clearPaymentAttempt();
          // Reset contribution result to show wizard again (not broken success screen)
          this.wizardService.contributionResult.set(null);
          this.error.set('Failed to process payment. Please try again.');
        },
      });
  }

  dismissCanceledMessage(): void {
    this.paymentCanceled.set(false);
  }
}
